'use client';

import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { createThemeFromTokens } from '@fast-computing/fast-graphics/mui-theme';

const selectedTheme = createThemeFromTokens('atlas', {
  withComponentDefaults: true,
});

type IntroDialogProps = {
  open: boolean;
  onStart: () => void;
};

export default function IntroDialog({ open, onStart }: IntroDialogProps) {
  const [markdown, setMarkdown] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      return;
    }

    const loadDescription = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await fetch('/left_atrium_appendage/description.md', {
          cache: 'no-store',
        });

        if (!response.ok) {
          throw new Error(`Unable to load description.md (${response.status})`);
        }

        const text = await response.text();
        setMarkdown(text);
      } catch (loadError) {
        setError(
          (loadError as Error).message || 'Unable to load app description.',
        );
      } finally {
        setLoading(false);
      }
    };

    loadDescription();
  }, [open]);

  return (
    <Dialog open={open} fullWidth maxWidth='sm'>
      <DialogTitle>About This Simulator</DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        ) : error ? (
          <Typography color='error'>{error}</Typography>
        ) : (
          <Box
            sx={{
              '& h1': { fontSize: '1.3rem', mb: 1.25, mt: 1.75 },
              '& h2': { fontSize: '1.05rem', mb: 0.85, mt: 1.5 },
              '& p': { fontSize: '0.92rem', mb: 1.1, lineHeight: 1.55 },
              '& blockquote': {
                fontSize: '0.72rem',
                color: 'text.secondary',
                borderLeft: '2px solid',
                borderColor: 'divider',
                pl: 1.5,
                my: 1.5,
                '& p': { fontSize: '0.72rem', mb: 0.4, lineHeight: 1.45 },
                '& strong': { fontSize: '0.72rem' },
              },
            }}
          >
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {markdown}
            </ReactMarkdown>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          variant='contained'
          onClick={onStart}
          sx={{
            backgroundColor: `${selectedTheme.palette.primary.main}`,
            fontFamily: `${selectedTheme.typography.fontFamily}`,
          }}
        >
          To Simulator
        </Button>
      </DialogActions>
    </Dialog>
  );
}
